import { AoInstance } from "../../ao/ao"
import { LiquidityTable, PoolLiquidity } from "../../ao/monitor-api"
import { PoolMap, PoolOverview } from "../../ao/router"
import { TokenDetails, TokenDetailsR } from "../../ao/tokendrop"
import ENV from "../../env"
import {
  PoolApiRow,
  PoolByTokensResult,
  PoolInfo,
  PoolInfoApiResponse,
} from "../../types/data-source"
import { createLogger } from "../../utils/logger"

const logger = createLogger("HB-DATA-SOURCE")

export class HBDataSource {
  constructor(private ao: AoInstance) {}

  async getReserves(pool: string, pair: [string, string]): Promise<[bigint, bigint]> {
    logger.log("Fetching reserves from HB API")
    const url = `${ENV.VITE_HB_API}/${pool}~process@1.0/now/reserves`

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000)

    try {
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        signal: controller.signal,
      })
      clearTimeout(timeoutId)

      if (!res.ok) {
        throw new Error(`Error fetching reserves from HB API: ${res.statusText}`)
      }

      const data = await res.json()
      const aReserves = data[pair[0]]
      const bReserves = data[pair[1]]

      if (!aReserves || !bReserves) {
        throw new Error(`Invalid reserves data from HB API: ${JSON.stringify(data)}`)
      }

      return [BigInt(aReserves), BigInt(bReserves)]
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof Error && error.name === "AbortError") {
        throw new Error("Request timed out after 5 seconds")
      }
      throw error
    }
  }

  async getPoolByTokens(
    pair: [string, string],
    feePercentageBps?: bigint,
  ): Promise<PoolByTokensResult> {
    logger.log(`Fetching pool by tokens ${pair} from HB API`)
    const response = await fetch(
      `${ENV.VITE_HB_API}/${ENV.VITE_AMM_FACTORY_CACHE_PROCESS}~process@1.0/now/pools_by_tokens/serialize~json@1.0`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    )

    if (!response.ok) {
      throw new Error(`Error fetching pool by tokens: ${response.statusText}`)
    }

    interface PoolsByTokensResponse {
      [key: string]: {
        [feePercentageBps: string]: string
      }
    }

    const data = (await response.json()) as PoolsByTokensResponse

    const poolKey = `${pair[0]}:${pair[1]}`
    const feeKey = feePercentageBps?.toString() || "25"

    if (data[poolKey]?.[feeKey]) {
      return {
        poolId: data[poolKey][feeKey],
        pending: false,
      }
    }

    const reversePoolKey = `${pair[1]}:${pair[0]}`
    if (data[reversePoolKey]?.[feeKey]) {
      return {
        poolId: data[reversePoolKey][feeKey],
        pending: false,
      }
    }

    throw new Error("Pool not found")
  }

  async getPoolMap(): Promise<PoolMap> {
    const response = await fetch(
      `${ENV.VITE_HB_API}/${ENV.VITE_AMM_FACTORY_CACHE_PROCESS}~process@1.0/now/pools/serialize~json@1.0`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    )

    if (!response.ok) {
      throw new Error(`Error fetching pool map from HB API: ${response.statusText}`)
    }

    const data = (await response.json()) as PoolMap

    if (!data) {
      throw new Error("No pool map data found")
    }

    return data
  }

  async getTokenDetails(tokenProcess: string): Promise<TokenDetails> {
    logger.log(`Fetching token details for ${tokenProcess} from HB API`)
    const url = `${ENV.VITE_HB_API}/${ENV.VITE_TOKENDROP_CACHE_PROCESS}~process@1.0/compute/tokens/${tokenProcess}`

    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })

    if (!res.ok) {
      throw new Error(`Error fetching info from HB API: ${res.statusText}`)
    }

    const tokenDetails = (await res.json()) as TokenDetailsR

    if (!tokenDetails) {
      throw new Error(`Token process ${tokenProcess} not found`)
    }

    return {
      ...tokenDetails,
      RenounceOwnership:
        typeof tokenDetails.RenounceOwnership === "string"
          ? tokenDetails.RenounceOwnership === "true"
          : tokenDetails.RenounceOwnership,
    }
  }

  async getGlobalLiquidity(): Promise<PoolLiquidity[]> {
    logger.log(`Fetching Global Liquidity from HB API`)
    const url = `${ENV.VITE_HB_API}/${ENV.VITE_GL_PROCESS}~process@1.0/compute/liquidity_table`

    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })

    if (!res.ok) {
      throw new Error(`Error fetching info from HB API: ${res.statusText}`)
    }

    const liquidityTable = (await res.json()) as LiquidityTable

    const pools: PoolLiquidity[] = Object.entries(liquidityTable).map(([key, pool]) => {
      return {
        amm_base_token: key,
        amm_token0: pool.Token_0,
        amm_quote_token: pool.Token_1,
        amm_name: key,
        amm_process: key,
        reserves_0: pool.Reserves_0,
        reserves_1: pool.Reserves_1,
        pool_fee_bps: 0, // Assuming fee is not provided in the response
        amm_status: "public", // Assuming status is public
        amm_discovered_at_ts: pool.Updated_At,
        amm_token1: pool.Token_1,
        token_0_decimals: parseInt(pool.Token_0_Denominator, 10),
        token_1_decimals: parseInt(pool.Token_1_Denominator, 10),
      }
    })

    return pools
  }

  async getTokensFromAMMFactory(): Promise<string[]> {
    logger.log(`Fetching tokens from AMM factory`)
    const url = `${ENV.VITE_HB_API}/${ENV.VITE_AMM_FACTORY_CACHE_PROCESS}~process@1.0/compute/tokens`

    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })

    if (!res.ok) {
      throw new Error(`Error fetching tokens from AMM factory: ${res.statusText}`)
    }

    const data = (await res.json()) as string[]
    return data || []
  }

  async getPoolInfo(poolId: string): Promise<PoolInfo> {
    logger.log(`Fetching pool info for ${poolId} from HB API`)
    const url = `${ENV.VITE_HB_API}/${poolId}~process@1.0/compute/general`

    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })

    if (!res.ok) {
      throw new Error(`Error fetching pool info from HB API: ${res.statusText}`)
    }

    const data = (await res.json()) as PoolInfoApiResponse

    if (!data) {
      throw new Error(`Pool info for ${poolId} not found`)
    }

    return {
      id: poolId,
      denomination: parseInt(data.Denomination, 10),
      ticker: data.Ticker,
      logo: data.Logo,
      name: data.Name,
      tokenA: data.TokenA,
      tokenB: data.TokenB,
      totalSupply: BigInt(data.TotalSupply),
      type: data.Type,
      fee: BigInt(data.FeeBps),
    }
  }

  async getBalance(tokenId: string, user: string): Promise<bigint> {
    logger.log(`Fetching balance for token ${tokenId} and user ${user} from HB API`)
    const url = `${ENV.VITE_HB_API}/${tokenId}~process@1.0/compute/balances/${user}`

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 2000)

    try {
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      })
      clearTimeout(timeoutId)

      if (!res.ok) {
        throw new Error(`Error fetching balance from HB API: ${res.statusText}`)
      }

      const data = (await res.text()) as string
      return BigInt(data || 0)
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof Error && error.name === "AbortError") {
        throw new Error("Request timed out after 2 seconds")
      }
      throw error
    }
  }

  async setCronForProcess(processId: string, every: string): Promise<string> {
    logger.log(`Setting cron for process ${processId} in HB API`)
    const url = `${ENV.VITE_HB_API}/~cron@1.0/every?cron-path=/${processId}~process@1.0/now&every=${every}`

    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })

    if (!res.ok) {
      throw new Error(`Error setting cron for process ${processId}: ${res.statusText}`)
    }

    const data = await res.json()
    return data.message || "Cron set successfully"
  }

  async getApprovedTokens(): Promise<string[]> {
    logger.log(`Fetching approved tokens from HB API`)
    const url = `${ENV.VITE_HB_API}/${ENV.VITE_DEXI_PROCESS}~process@1.0/compute/cache/community_approved_tokens`

    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })

    if (!res.ok) {
      throw new Error(`Error fetching approved tokens from HB API: ${res.statusText}`)
    }

    const data = (await res.json()) as string[]
    return data || []
  }
}
